import api from './authService';

class AnalyticsService {
  constructor() {
    this.eventQueue = [];
    this.statsQueue = [];
    this.batchInterval = null;
    this.statsCollectionInterval = null;
    this.meetingId = null;
    this.userId = null;
    this.previousStats = {}; // Store previous stats for bitrate calculation
  }

  // Initialize analytics for a meeting
  init(meetingId, userId) {
    this.meetingId = meetingId;
    this.userId = userId;
    this.eventQueue = [];
    this.statsQueue = [];
    
    // Send batched events every 10 seconds
    this.batchInterval = setInterval(() => {
      this.flushEvents();
      this.flushStats();
    }, 10000);
  }

  // Stop analytics collection
  stop() {
    if (this.batchInterval) {
      clearInterval(this.batchInterval);
      this.batchInterval = null;
    }
    if (this.statsCollectionInterval) {
      clearInterval(this.statsCollectionInterval);
      this.statsCollectionInterval = null;
    }
    
    // Flush any remaining data
    this.flushEvents();
    this.flushStats();
    
    // Clear previous stats
    this.previousStats = {};
  }

  // Track an analytics event
  trackEvent(eventType, payload = {}) {
    if (!this.meetingId) return;

    this.eventQueue.push({
      type: eventType,
      ts: Date.now(),
      userId: this.userId,
      payload
    });

    // If queue gets too large, flush immediately
    if (this.eventQueue.length >= 50) {
      this.flushEvents();
    }
  }

  // Start collecting WebRTC stats
  startStatsCollection(peerConnection) {
    if (!peerConnection || !this.meetingId) return;

    // Collect stats every 5 seconds
    this.statsCollectionInterval = setInterval(async () => {
      try {
        const stats = await peerConnection.getStats();
        const sample = this.parseWebRTCStats(stats);
        
        if (sample) {
          this.statsQueue.push({
            ts: Date.now(),
            ...sample
          });

          // If queue gets too large, flush immediately
          if (this.statsQueue.length >= 100) {
            this.flushStats();
          }
        }
      } catch (error) {
        console.error('Error collecting WebRTC stats:', error);
      }
    }, 5000);
  }

  // Stop collecting WebRTC stats
  stopStatsCollection() {
    if (this.statsCollectionInterval) {
      clearInterval(this.statsCollectionInterval);
      this.statsCollectionInterval = null;
    }
  }

  // Parse WebRTC stats into our format
  parseWebRTCStats(stats) {
    let sample = {
      rtt: null,
      jitter: null,
      packetLossPct: null,
      bitrateSendKbps: null,
      bitrateRecvKbps: null,
      fps: null,
      width: null,
      height: null,
      audioLevel: null,
      iceState: null
    };

    const currentTime = Date.now();

    stats.forEach(report => {
      // RTT and jitter from candidate-pair
      if (report.type === 'candidate-pair' && report.state === 'succeeded') {
        if (report.currentRoundTripTime) {
          sample.rtt = report.currentRoundTripTime * 1000; // Convert to ms
        }
      }

      // Inbound RTP stats (receiving)
      if (report.type === 'inbound-rtp') {
        if (report.jitter) {
          sample.jitter = report.jitter * 1000; // Convert to ms
        }
        if (report.packetsLost && report.packetsReceived) {
          sample.packetLossPct = (report.packetsLost / (report.packetsLost + report.packetsReceived)) * 100;
        }
        if (report.bytesReceived && report.timestamp) {
          // Calculate bitrate from delta
          const key = `recv_${report.id}`;
          if (this.previousStats[key]) {
            const bytesDelta = report.bytesReceived - this.previousStats[key].bytes;
            const timeDelta = (currentTime - this.previousStats[key].timestamp) / 1000; // seconds
            if (timeDelta > 0) {
              sample.bitrateRecvKbps = Math.round((bytesDelta * 8) / (timeDelta * 1024));
            }
          }
          this.previousStats[key] = { bytes: report.bytesReceived, timestamp: currentTime };
        }
      }

      // Outbound RTP stats (sending)
      if (report.type === 'outbound-rtp') {
        if (report.bytesSent && report.timestamp) {
          // Calculate bitrate from delta
          const key = `send_${report.id}`;
          if (this.previousStats[key]) {
            const bytesDelta = report.bytesSent - this.previousStats[key].bytes;
            const timeDelta = (currentTime - this.previousStats[key].timestamp) / 1000; // seconds
            if (timeDelta > 0) {
              sample.bitrateSendKbps = Math.round((bytesDelta * 8) / (timeDelta * 1024));
            }
          }
          this.previousStats[key] = { bytes: report.bytesSent, timestamp: currentTime };
        }
        if (report.framesPerSecond) {
          sample.fps = report.framesPerSecond;
        }
      }

      // Video track stats
      if (report.type === 'track' && report.kind === 'video') {
        if (report.frameWidth) sample.width = report.frameWidth;
        if (report.frameHeight) sample.height = report.frameHeight;
      }

      // Audio track stats
      if (report.type === 'track' && report.kind === 'audio') {
        if (report.audioLevel !== undefined) {
          sample.audioLevel = report.audioLevel;
        }
      }

      // ICE connection state
      if (report.type === 'transport') {
        if (report.state) {
          sample.iceState = report.state;
        }
      }
    });

    // Only return if we have at least some data
    if (sample.rtt || sample.jitter || sample.audioLevel !== null) {
      return sample;
    }

    return null;
  }

  // Flush events to server
  async flushEvents() {
    if (this.eventQueue.length === 0 || !this.meetingId) return;

    const eventsToSend = [...this.eventQueue];
    this.eventQueue = [];

    try {
      await api.post(
        '/analytics/events',
        {
          meetingId: this.meetingId,
          events: eventsToSend
        }
      );
    } catch (error) {
      console.error('Error sending analytics events:', error);
      // On error, don't retry to avoid accumulating too much data
    }
  }

  // Flush stats to server
  async flushStats() {
    if (this.statsQueue.length === 0 || !this.meetingId) return;

    const statsToSend = [...this.statsQueue];
    this.statsQueue = [];

    try {
      await api.post(
        '/analytics/webrtc-stats',
        {
          meetingId: this.meetingId,
          userId: this.userId,
          samples: statsToSend
        }
      );
    } catch (error) {
      console.error('Error sending RTC stats:', error);
      // On error, don't retry to avoid accumulating too much data
    }
  }

  // Fetch alert thresholds from server
  async getAlertThresholds() {
    try {
      const response = await api.get('/analytics/alert-thresholds');
      return response.data;
    } catch (error) {
      console.error('Error fetching alert thresholds:', error);
      // Return default thresholds on error
      return {
        alertQualityThreshold: 60,
        alertJitterThresholdMs: 30,
        alertRttThresholdMs: 300,
        alertPacketLossThresholdPct: 5.0,
        emailSummariesEnabled: true,
        inCallAlertsEnabled: true
      };
    }
  }

  // Update alert thresholds (admin only)
  async updateAlertThresholds(thresholds) {
    try {
      const response = await api.put(
        '/analytics/alert-thresholds',
        thresholds
      );
      return response.data;
    } catch (error) {
      console.error('Error updating alert thresholds:', error);
      throw error;
    }
  }

  // Send email summary for a meeting
  async sendMeetingSummary(meetingId) {
    try {
      const response = await api.post(
        `/analytics/meetings/${meetingId}/send-summary`,
        {}
      );
      return response.data;
    } catch (error) {
      console.error('Error sending meeting summary:', error);
      throw error;
    }
  }

  // Check if stats exceed alert thresholds
  checkForAlerts(stats, thresholds) {
    const alerts = [];

    if (stats.jitter > thresholds.alertJitterThresholdMs) {
      alerts.push({
        type: 'jitter',
        severity: 'warning',
        message: `High jitter: ${stats.jitter.toFixed(1)}ms`,
        value: stats.jitter
      });
    }

    if (stats.rtt > thresholds.alertRttThresholdMs) {
      alerts.push({
        type: 'rtt',
        severity: 'warning',
        message: `High latency: ${stats.rtt.toFixed(1)}ms`,
        value: stats.rtt
      });
    }

    if (stats.packetLossPct > thresholds.alertPacketLossThresholdPct) {
      alerts.push({
        type: 'packet_loss',
        severity: 'error',
        message: `High packet loss: ${stats.packetLossPct.toFixed(2)}%`,
        value: stats.packetLossPct
      });
    }

    return alerts;
  }
}

// Export singleton instance
const analyticsService = new AnalyticsService();
export default analyticsService;
